import React from 'react';
import './chuprynenko.scss';
import {Link, useLocation} from 'react-router-dom';
import {ErrorBoundary} from 'react-error-boundary';
import Skeleton from './../../../Skeleton';
import {useNavigate} from 'react-router-dom';
import {graphql, useLazyLoadQuery, useFragment} from 'react-relay/hooks';
import searchFilter from '../../../../searchFilter';
import {Helmet} from 'react-helmet-async';
import SeoMetaTags from './../../../seo-meta-tags';
import Marked from './../../../Marked';
import i18n from '../../../../i18n';
import useQuery from '../../../../useQuery';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';
import useNow from '../../../../useNow';
import {adopt} from '../../../../math';
import {IN, ENTITY_TYPE} from '../../../../constants';
import Mini from './../../mini/Mini';
import IconMaster from '../../../../assets/icons-payment/icon-master.svg';
import IconVisa from '../../../../assets/icons-payment/icon-visa.svg';
import IconApplePay from '../../../../assets/icons-payment/icon-apple-pay.svg';
import IconGooglePay from '../../../../assets/icons-payment/icon-google-pay.svg';
import ErrorFallback from '../../../ErrorFallback';
import Browser from '../../../Browser';
import Ticket01 from '../../../../assets/icons0/ticket-01.svg';
import ShoppingBag03 from '../../../../assets/icons0/shopping-bag-03.svg';
import DovgenkoCentrSeatMap from './dovgenko-centr-seat-map';
import LogoWhiteDarkModeTrue from '../../../../assets/logo-white-dark-mode-true.svg'
import {useInstantDispatch} from '../../../../contexts/instant';
import ChevronUp from '../../../../assets/icons0/chevron-up.svg';
import splitPrice from '../../../../pages/checkout/splitPrice';
import moment from 'moment';
import CartAddCircle from '../../../../assets/icons/cart-add-circle.svg';
import CartMinusCircle from '../../../../assets/icons/cart-minus-circle.svg';
import XClose from '../../../../assets/icons0/x-close.svg';
import SafetyMark from '../../../../assets/icons/safety-mark.svg';
import getLocaleQueryParams from '../../../../utilities/get-locale-query-params';
import ServiceNotFound from '../../../../pages/warp/service-not-found';
import {localCartLandImpulseQuery} from '../../../../pages/warp/local-cart/graphql';

const legal = 'https://www.ottry.com/legal';
const facebookLink = 'https://www.facebook.com/OTTRYcom';
const formLink = 'https://docs.google.com/forms/d/e/1FAIpQLSesrYhmBpJV-mvZD9AHOM79MyP8CcgSzFANSQWNby__dilgRw/viewform';
const year = new Date().getFullYear();

const OrderItem = ({endpointId, serviceById, removeEndpointFromCart}) => {
    const {locale = 'uk'} = useQuery();
    const {endpointById} = useLazyLoadQuery(
        graphql`
            query chuprynenkoOrderItemQuery($endpointId: String) {
                endpointById(id: $endpointId) {
                    id
                    name
                    price
                    nameUk
                }
            }
        `,
        {endpointId: endpointId}
    );
    const service = useFragment(
        graphql`
            fragment chuprynenkoOrderItemService_serviceById on Service {
                id
                name
                target
                nameUk
            }
        `,
        serviceById
    );    
    return (
        <div className='order-item'>
            <p className='text-md color-gray-700 mw768-text-lg'>{i18n(service, 'name', locale)} | <span className='white-space-nowrap'>{i18n(endpointById, 'name', locale)}</span></p>
            {moment(service.target).isValid() && <p className='text-sm color-gray-500 padding-top-0dot25rem'>{moment(service.target).format('DD.MM.YYYY')}</p>}
            <div className='order-item__footer'>
                <div className='order-item__actions'>
                    <div className='order-item__actions-group'>
                        <div className='btn-icon btn-icon--primary' onClick={() => removeEndpointFromCart(endpointId)}>
                            <CartMinusCircle className='order-item__actions-svg'/>
                        </div>
                        <p className='order-item__amount color-primary-600 text-md medium'>1</p>
                        <div className='btn-icon btn-icon--disabled'>
                            <CartAddCircle className='order-item__actions-svg'/>
                        </div>
                    </div>
                </div>
                <p className='text-md color-gray-900 medium mw768-text-lg'>
                    {endpointById.price.toFixed(2)} <FormattedMessage defaultMessage='UAH' />
                </p>
            </div>
        </div>
    )
};

const CartFooterDesktopContent = ({localCartState, deferred, serviceById, checkout}) => {
    return (
        <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
            <div className='cart-footer__details cart-footer__details--visible'>
                <div className='display-flex align-items-center'>
                    <div className='text-sm color-gray-500 flex-1'>
                        <span><FormattedMessage defaultMessage='Services fee'/></span>
                    </div>
                    <React.Suspense fallback={
                        <div className='text-md medium color-gray-700 opacity-0dot4 pointer-events-none'>
                            <span>0.<span className='text-xs'>00 </span></span>
                            <FormattedMessage defaultMessage='UAH'/>
                        </div>
                    }>
                        <ServiceFee{...{localCartState, serviceId: serviceById.id, deferred}}/>
                    </React.Suspense>
                </div>
            </div>
            <div className='display-flex flex-direction-row justify-content-space-between align-items-center flex-1 padding-bottom-0dot75rem'>
                <div className='text-sm color-gray-700'>
                    <FormattedMessage defaultMessage='Total'/>
                </div>
                <React.Suspense fallback={<span className='text-lg semibold color-gray-900 opacity-0dot4'>0.00 <FormattedMessage defaultMessage='UAH'/></span>}>
                    <Amount {...{localCartState, serviceId: serviceById.id, deferred, classes: 'text-lg semibold color-gray-900'}}/>
                </React.Suspense>
            </div>
            <div className='cart-footer__payment padding-bottom-0dot75rem'>
                <div className='btn btn--xl btn--primary-gradient btn--fluid' onClick={() => checkout()}>
                    <FormattedMessage defaultMessage='Checkout'/>
                </div>
            </div>
            <div className='padding-top-1rem padding-bottom-1rem display-flex justify-content-center'>
                <SafetyMark className='display-block margin-right-0dot75rem'/>
                <p className='text-md color-gray-900'><FormattedMessage defaultMessage='100% Safe and Secure'/></p>
            </div>
        </ErrorBoundary>
    )
}

const Cart = ({removeEndpointFromCart, serviceById, localCartState, cartVisible, closeCart}) => {
    const localCartStateDeferred = React.useDeferredValue(localCartState);
    return (
        <div className={classNames('pop-up-cart-backdrop', {'show': cartVisible})}>
            <div className={classNames('pop-up-cart position-fixed background-color-white bottom-0 right-0', {'pop-up-cart--visible': cartVisible})}>
                <div className='position-sticky top-0 background-color-white'>
                    <div className='display-flex justify-content-flex-end padding-1dot25rem-1rem mw768-padding-1dot25rem-2dot5rem'>
                        <XClose className='display-block height-1dot5rem width-1dot5rem color-gray-500 cursor-pointer' onClick={closeCart}/>
                    </div>
                    <h3 className='display-sm semibold color-gray-900 padding-left-1dot25rem padding-right-1dot25rem mw768-padding-left-2dot5rem mw768-padding-right-2dot5rem padding-bottom-2rem'>
                        <FormattedMessage defaultMessage='Cart'/>
                    </h3>
                </div>
                <div className='pop-up-cart__content padding-left-1dot25rem padding-right-1dot25rem mw768-padding-left-2dot5rem mw768-padding-right-2dot5rem'>
                    {localCartState.map(endpointId =>
                        <React.Suspense key={endpointId} fallback={<Skeleton/>}>
                            <OrderItem {...{endpointId, serviceById, removeEndpointFromCart}}/>
                        </React.Suspense>
                    )}
                </div>
                <div className='hide-mobile'>
                    <CartFooter {...{localCartState, serviceById}}>
                        {checkout =>
                            <CartFooterDesktopContent {...{localCartState, serviceById, deferred: localCartStateDeferred !== localCartState, checkout}}/>
                        }
                    </CartFooter>
                </div>
            </div>
        </div>
    )
}

const Amount = ({localCartState, serviceId, deferred, classes}) => {
    const request = React.useMemo(() => ({
        picks: localCartState.map((e) => ({endpoint: e, service: serviceId, quantity: 1}))
    }), [localCartState]);
    const {landImpulse} = useLazyLoadQuery(
        localCartLandImpulseQuery,
        {request}
    );
    return (
        <span className={classNames(classes, {'opacity-0dot4 pointer-events-none' : deferred})}>
            {landImpulse && landImpulse.solves[0] ? landImpulse.solves[0].amount : '0.00'} <FormattedMessage defaultMessage='UAH'/>
        </span>
    )
}

const ServiceFee = ({deferred, localCartState, serviceId}) => {
    const request = React.useMemo(() => ({
        picks: localCartState.map((e) => ({endpoint: e, service: serviceId, quantity: 1}))
    }), [localCartState]);
    const {landImpulse} = useLazyLoadQuery(
        localCartLandImpulseQuery,
        {request}
    );
    const serviceFee = React.useMemo(() => landImpulse.solves[0] ? splitPrice(landImpulse.solves[0].serviceFeeAmount) : {whole: '0', fraction: '00'}, [landImpulse]);
    return (
        <div className={classNames('text-sm mw768-text-md medium color-gray-700', {'opacity-0dot4 pointer-events-none': deferred})}>
            <span><span>{serviceFee.whole}.</span><span className='text-xs'>{serviceFee.fraction} </span></span>
            <FormattedMessage defaultMessage='UAH'/>
        </div>
    )
}

const CartFooterMobileContent = ({flag, localCartState, serviceById, deferred, setFlag, checkout}) => {
    return (
        <>
            <div className={classNames('cart-footer__details', {'cart-footer__details--visible': flag})}>
                <div className='display-flex'>
                    <div className='text-sm color-gray-500 flex-1'>
                        <span><FormattedMessage defaultMessage='Services fee'/></span>
                    </div>
                    <React.Suspense fallback={
                        <div className='text-md medium color-gray-700 opacity-0dot4 pointer-events-none'>
                            <span>0.<span className='text-xs'>00 </span></span>
                            <FormattedMessage defaultMessage='UAH'/>
                        </div>
                    }>
                        <ServiceFee{...{localCartState, serviceId: serviceById.id, deferred}}/>
                    </React.Suspense>
                </div>   
            </div>
            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                <div className='cart-footer__payment display-flex align-items-center flex-direction-row-column'>
                    <div className='cart-footer__action flex-1 display-flex' onClick={() => setFlag(!flag)}>
                        <div className='padding-top-0dot1875rem'>
                            <ChevronUp className={classNames('cart-footer__indicator color-primary-500 height-1dot25rem width-1dot25rem display-block', {'cart-footer__indicator--active': flag})}/>
                            <ShoppingBag03 className='color-primary-500 height-1dot25rem width-1dot25rem display-block'/>
                        </div>
                        <div className='cart-footer__total justify-content-space-between flex-1'>
                            <div className='text-sm color-gray-700'>
                                <FormattedMessage defaultMessage='Total'/>
                            </div>
                            <React.Suspense fallback={<span className='text-lg semibold color-gray-900 opacity-0dot4'>0.00 <FormattedMessage defaultMessage='UAH'/></span>}>
                                <Amount {...{localCartState, serviceId: serviceById.id, deferred, classes: 'text-lg semibold color-gray-900'}}/>
                            </React.Suspense>
                        </div>
                    </div>
                    <div className='btn btn--2xl btn--primary-gradient' onClick={() => checkout()}>
                        <FormattedMessage defaultMessage='Checkout'/>
                    </div>
                </div>
            </ErrorBoundary>
        </>
    )
}

const CartFooter = ({localCartState, serviceById, children, hide}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useInstantDispatch();
    const [isPending, startTransition] = React.useTransition();
    const checkout = React.useCallback(() => {
        startTransition(() => {
            dispatch({type: 'reset', payload: []});
            localCartState.forEach(endpointId => {
                dispatch({type: 'add-several', payload: {
                    endpointId: endpointId,
                    serviceId: serviceById.id
                }});
            })
            navigate(`/checkout/ticketing-instant/impulse${searchFilter(location.search)}`);
        })
    }, [localCartState]);
    return (
        <div className='cart-footer position-fixed bottom-0 left-0 width-100percent z-index-1'>
            {children(checkout)}
        </div>
    )
}

const CartButton = ({serviceById, localCartState, removeEndpointFromCart}) => {
    const localCartStateDeferred = React.useDeferredValue(localCartState);
    const [cartVisibility, setCartVisibility] = React.useState(false);
    const setCartVisibilityCallback = React.useCallback((value) => setCartVisibility(value));
    React.useEffect(() => {
        if (!localCartState.length) {
            setCartVisibility(false);
        }
    }, [localCartState])
    return (
        <>
            <div className='display-none mw768-display-block min-width-11rem'>
                <div className={classNames('btn btn--lg btn--primary-gradient btn--fluid', {'btn--disabled': !localCartState.length})} onClick={() => setCartVisibility(true)}>
                    {localCartState.length ?
                        <React.Suspense fallback={
                            <>
                                <ShoppingBag03 className='display-block height-1dot25rem margin-right-0dot5rem opacity-0dot4'/>
                                <span className='opacity-0dot4'>0.00 <FormattedMessage defaultMessage='UAH'/></span>
                            </>
                        }>
                            <ShoppingBag03 className={classNames('display-block height-1dot25rem margin-right-0dot5rem', {'opacity-0dot4': localCartStateDeferred !== localCartState})}/>
                            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                <Amount {...{localCartState, serviceId: serviceById.id, deferred: localCartStateDeferred !== localCartState}}/>
                            </ErrorBoundary>
                        </React.Suspense>
                        :
                        <>
                            <ShoppingBag03 className='display-block height-1dot25rem margin-right-0dot5rem'/>
                            <span>0.00 <FormattedMessage defaultMessage='UAH'/></span>
                        </>
                    }
                </div>
            </div>
            {!!localCartState.length && 
                <>
                    <div className='mw768-display-none'>
                        <CartFooter {...{localCartState, serviceById}}>
                            {checkout =>
                                <CartFooterMobileContent {...{localCartState, serviceById, deferred: localCartStateDeferred !== localCartState, checkout, flag: cartVisibility, setFlag: setCartVisibilityCallback}}/>
                            }
                        </CartFooter>
                    </div>
                    <Cart {...{cartVisible: cartVisibility, removeEndpointFromCart, serviceById, localCartState, deferred: localCartStateDeferred !== localCartState, closeCart: () => setCartVisibilityCallback(false)}}/>
                </>
            }
        </>
    )
}

const ChuprynenkoTickets = ({serviceById}) => {
    const [localCartState, setLocalCartState] = React.useState([]);
    const service = useFragment(
        graphql`
            fragment chuprynenkoTickets_serviceById on Service {
                id
                endpoints {
                    id
                    price
                    tags
                    quantity
                }
                
            }
        `,
        serviceById
    );

    const toggleEndpoint = (endpointId) => {
        if (localCartState.includes(endpointId)) {
            setLocalCartState(localCartState.filter(e => e !== endpointId));
        } else {
            const newLocalState = localCartState.slice();
            newLocalState.push(endpointId);
            setLocalCartState(newLocalState);
        }
    };
    const removeEndpointFromCart = React.useCallback((endpointId) => {
        setLocalCartState(localCartState.filter(e => e !== endpointId));
    })
    return (
        <>
            <div className='padding-top-2rem padding-bottom-1rem border-bottom-1px-solid border-color-gray-700 display-flex justify-content-space-between align-items-center background-color-black position-sticky top-0 z-index-1'>
                <div className='display-flex'>
                    <Ticket01 className='display-block height-1dot5rem color-primary-500'/>
                    <p className='margin-left-0dot75rem text-lg medium'><FormattedMessage defaultMessage='Tickets' /></p>
                </div>
                <div>
                    <Browser>
                        <CartButton {...{serviceById, localCartState, removeEndpointFromCart}}/>
                    </Browser>
                </div>
            </div>
            <div className='padding-top-2rem'>
                <DovgenkoCentrSeatMap {...{service, toggleEndpoint, localCartState}}/>
            </div>
        </>
    )
}

const Chuprynenko = ({id}) => {
    const data = useLazyLoadQuery(
        graphql`
            query chuprynenkoQuery($id: String) {
                serviceById(id: $id) {
                    id
                    name
                    nameUk
                    details
                    detailsUk
                    description
                    start
                    end
                    strategy
                    every
                    endpoints {
                        id
                        quantity
                    }
                    ...chuprynenkoTickets_serviceById
                    ...chuprynenkoOrderItemService_serviceById
                }
            }
        `,
        {id}
    );
    const showTicketsBoxBtn = data.serviceById.description && data.serviceById.description.includes('btnAnchorTickets');
    const ticketsBoxRef = React.useRef();
    const scrollToTickets = () => {
        ticketsBoxRef.current.scrollIntoView({
            behavior: "smooth",
        });
    }
    const {locale = 'uk', more} = useQuery();
    const cover = React.useMemo(() => {
        if (i18n(data.serviceById, 'details', locale)) {
            const regex = /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png)/i;
            const found = i18n(data.serviceById, 'details', locale).match(regex);
            return found && found[0];
        }
    }, [data, locale]);
    const now = useNow();
    const {state} = React.useMemo(() => adopt(now, data.serviceById.start, data.serviceById.end, data.serviceById.strategy,
        data.serviceById.every), [now, data.serviceById]);
    return (
        <>
        <SeoMetaTags {...{
            meta: {
                title: i18n(data.serviceById, 'name', locale),
                description: i18n(data.serviceById, 'name', locale)
            }
        }}/>
        <Helmet>
            <meta property='og:image' content={cover}/>
            <meta property="og:type" content="article"/>
        </Helmet>
        <div>
            {showTicketsBoxBtn &&
                <div className='position-absolute top-1dot25rem right-1dot25rem'>
                    <div className='button primary' onClick={() => scrollToTickets()}><FormattedMessage defaultMessage='Tickets'/></div>
                </div>
            }
            <div className='view'>
                <div className='block'>
                    <div className='font-size-2rem font-weight-bold margin-bottom-1rem color-gray-100 padding-top-2rem mw768-padding-top-4rem'>
                        {i18n(data.serviceById, 'name', locale)}
                    </div>
                    <Marked {...{content: i18n(data.serviceById, 'details', locale)}}/>
                    <div className="display-flex flex-wrap-wrap">
                        <div className="flex-grow-1 flex-basis-300px margin-bottom-1rem">
                            {i18n(data.serviceById, 'details', locale) && i18n(data.serviceById, 'details', locale).endsWith('!') ?
                                <>
                                    <p><FormattedMessage defaultMessage='Sales were closed'/></p>
                                </>
                                :
                                <>
                                    <Mini {...{
                                        start: data.serviceById.start,
                                        end: data.serviceById.end,
                                        strategy: data.serviceById.strategy,
                                        every: data.serviceById.every,
                                        entityType: ENTITY_TYPE.service
                                    }}/>
                                    {state === IN &&
                                        <div ref={ticketsBoxRef}>
                                            {data.serviceById.endpoints.some(e => e.quantity) && <ChuprynenkoTickets {...{serviceById: data.serviceById}}/>}
                                            {!data.serviceById.endpoints.some(e => e.quantity) && <div>Квитки закінчилися</div>}
                                        </div>
                                    }
                                </>
                            }
                        </div>
                    </div>
                    <br/>
                    <p className='font-size-0dot8rem'><FormattedMessage defaultMessage='* After payment, a letter with a QR code will be sent to your e-mail address, which must be presented at the entrance. Please note that in the case of using the Gmail mail service, the letter with the ticket may end up in the "Alerts", "Promotions" or "Social networks" folder.'/></p>
                </div>
            </div>
        </div>
        </>
    )
};

export default React.memo(({id}) => {
    const location = useLocation();
    const {locale = 'uk'} = useQuery();
    return (
        <div className='chuprynenko black'>
            <div className='chuprynenko__header'>
                <Link to={`/events${searchFilter(location.search)}`}>
                    <LogoWhiteDarkModeTrue className='height-2rem'/>
                </Link>
            </div>
            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                <ErrorBoundary {...{FallbackComponent: ServiceNotFound('dark')}}>
                    <React.Suspense fallback={
                        <div className='view'>
                            <div className='block'>
                                <Skeleton/>
                            </div>
                        </div>
                    }>
                        <Chuprynenko {...{id}}/>
                    </React.Suspense>
                </ErrorBoundary>
            </ErrorBoundary>
            <div className='chuprynenko__footer'>
                <div className='chuprynenko__footer-line'>
                    <div className='chuprynenko__footer-line-left'>&nbsp;</div>
                    <Link to={`/${searchFilter(location.search)}`}>Ottry</Link>
                    <div className='chuprynenko__footer-line-right'>&nbsp;</div>
                </div>
                <div className='display-flex justify-content-center align-items-baseline margin-bottom-0dot5rem'>
                    <a
                        className={classNames('font-weight-bold', 'margin-right-0dot5rem', {'font-size-1dot2rem': locale === 'uk'})}
                        href={`${location.pathname}${getLocaleQueryParams(location.search, 'uk')}`}
                    >Uk</a>
                    {' '}
                    <a
                        className={classNames('font-weight-bold', {'font-size-1dot2rem': locale === 'en'})}
                        href={`${location.pathname}${getLocaleQueryParams(location.search, 'en')}`}
                    >En</a>
                </div>
                <div className='chuprynenko__payment margin-bottom-1rem'>
                    <IconMaster className='display-block margin-right-0dot5rem height-2rem mw768-height-2dot5rem'/>
                    <IconVisa className='display-block margin-right-0dot5rem height-2rem mw768-height-2dot5rem'/>
                    <IconApplePay className='display-block margin-right-0dot5rem height-2rem mw768-height-2dot5rem'/>
                    <IconGooglePay className='display-block height-2rem mw768-height-2dot5rem'/>
                </div>
                <div className='chuprynenko__footer-content'>
                    <div className='chuprynenko__footer-info'>
                        <a
                            href={formLink}
                            target='_blank'
                            rel='noopener noreferrer'
                        ><FormattedMessage defaultMessage='I want to sell online'/></a>
                        <a href={facebookLink} target='_blank' rel='noopener noreferrer'>
                            <FormattedMessage defaultMessage='Report an issue'/>
                        </a>
                        <Link to={`/rules${searchFilter(location.search)}`}><FormattedMessage defaultMessage='Rules'/></Link>
                        <Link to={`/terms${searchFilter(location.search)}`}><FormattedMessage defaultMessage='Terms of service'/></Link>
                        <Link to={`/privacy${searchFilter(location.search)}`}><FormattedMessage defaultMessage='Privacy policy'/></Link>
                    </div>
                </div>
                <div className='chuprynenko__footer-rights'>
                    © {year} Ottry. All rights reserved.
                </div>
            </div>
        </div>
    );
});